export const SUPPORTED_LOCALES = ["en", "zh-CN", "fr", "de", "it", "nl", "es"] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

export interface LocaleMetadata {
  code: Locale;
  nativeName: string;
  languageName: string;
}

export const LOCALES: readonly LocaleMetadata[] = [
  { code: "en", nativeName: "English", languageName: "English" },
  { code: "zh-CN", nativeName: "简体中文", languageName: "Simplified Chinese" },
  { code: "fr", nativeName: "Français", languageName: "French" },
  { code: "de", nativeName: "Deutsch", languageName: "German" },
  { code: "it", nativeName: "Italiano", languageName: "Italian" },
  { code: "nl", nativeName: "Nederlands", languageName: "Dutch" },
  { code: "es", nativeName: "Español", languageName: "Spanish" },
] as const;

const localeSet = new Set<string>(SUPPORTED_LOCALES);

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && localeSet.has(value);
}

export function matchLocale(value: string | null | undefined): Locale | null {
  if (value === null || value === undefined) return null;
  const normalized = value.trim().replaceAll("_", "-");
  if (isLocale(normalized)) return normalized;
  const lower = normalized.toLowerCase();
  if (lower === "zh" || lower.startsWith("zh-")) return "zh-CN";
  for (const locale of SUPPORTED_LOCALES) {
    if (locale !== "zh-CN" && (lower === locale || lower.startsWith(`${locale}-`))) return locale;
  }
  return null;
}

export function detectLocale(
  stored: string | null | undefined,
  browserLanguages: readonly string[] = [],
): Locale {
  const saved = isLocale(stored) ? stored : null;
  if (saved !== null) return saved;
  for (const language of browserLanguages) {
    const matched = matchLocale(language);
    if (matched !== null) return matched;
  }
  return "en";
}

export const LLM_LANGUAGE_INSTRUCTIONS: Readonly<Record<Locale, string>> = {
  en: "English",
  "zh-CN": "Simplified Chinese",
  fr: "French",
  de: "German",
  it: "Italian",
  nl: "Dutch",
  es: "Spanish",
};

export function formatNumber(locale: Locale, value: number, options?: Intl.NumberFormatOptions) {
  return new Intl.NumberFormat(locale, options).format(value);
}

export function formatDate(
  locale: Locale,
  value: Date | number | string,
  options?: Intl.DateTimeFormatOptions,
) {
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(locale, options).format(date);
}

export function interpolate(template: string, values: Readonly<Record<string, string | number>>) {
  return template.replace(/\{([a-zA-Z][\w]*)\}/gu, (match, key: string) =>
    Object.hasOwn(values, key) ? String(values[key]) : match,
  );
}

const en = {
  language: "Language",
  publicNavigation: "Public navigation",
  appNavigation: "Application navigation",
  mobileNavigation: "Mobile application navigation",
  loading: "Loading private evidence…",
  retry: "Reload",
  activities: "Activities",
  distance: "Distance",
  movingTime: "Moving time",
  needsAttention: "Needs attention",
  notRecorded: "Not recorded",
  yes: "Yes",
  no: "No",
  cycling: "Cycling",
  running: "Running",
  hiking: "Hiking",
  recorded: "Recorded",
  derived: "Calculated",
  corrected: "Corrected",
  missing: "Missing",
  unsupported: "Unsupported",
  insufficient: "Insufficient",
  source: "Source",
  calculated: "Calculated",
  correction: "Correction",
  none: "None",
  high: "High",
  medium: "Medium",
  low: "Low",
  unavailable: "Unavailable",
  reportTitle: "SportTrack activity report",
  limitationsHeading: "Limitations and data quality",
  noLimitations: "No additional limitations detected.",
  fixedShareTitle: "SportTrack fixed share",
  fixedShareDescription:
    "This page shows only the redacted content the athlete reviewed and fixed. Links can be forwarded, expire, or be revoked.",
  maplessShare: "Mapless share",
  maplessDescription: "No route geometry is included in this fixed snapshot.",
  routeOverview: "Redacted route overview",
  includedMetrics: "Included metrics",
  recipientBoundary: "This recipient page cannot access the original Activity or Source Asset.",
  eligibleActivity: "{count} eligible {sport} activity",
  eligibleActivities: "{count} eligible {sport} activities",
} as const;

export type MessageKey = keyof typeof en;
export type MessageCatalog = { readonly [K in MessageKey]: string };

const zhCN: MessageCatalog = {
  language: "语言",
  publicNavigation: "公共导航",
  appNavigation: "应用导航",
  mobileNavigation: "移动应用导航",
  loading: "正在加载私密证据…",
  retry: "重新加载",
  activities: "活动",
  distance: "距离",
  movingTime: "移动时间",
  needsAttention: "需处理",
  notRecorded: "未记录",
  yes: "是",
  no: "否",
  cycling: "骑行",
  running: "跑步",
  hiking: "徒步",
  recorded: "已记录",
  derived: "已计算",
  corrected: "已修正",
  missing: "缺失",
  unsupported: "不支持",
  insufficient: "数据不足",
  source: "来源",
  calculated: "已计算",
  correction: "修正",
  none: "无",
  high: "高",
  medium: "中",
  low: "低",
  unavailable: "不可用",
  reportTitle: "SportTrack 活动报告",
  limitationsHeading: "限制与数据质量",
  noLimitations: "没有检测到额外限制。",
  fixedShareTitle: "SportTrack 固定分享",
  fixedShareDescription:
    "此页面仅显示运动者审核并固定的脱敏内容。链接可被转发，也可能过期或被撤销。",
  maplessShare: "无地图分享",
  maplessDescription: "此固定快照不包含路线几何。",
  routeOverview: "脱敏路线概览",
  includedMetrics: "所含指标",
  recipientBoundary: "接收者页面无法访问原始活动或源文件。",
  eligibleActivity: "{count} 项符合条件的{sport}活动",
  eligibleActivities: "{count} 项符合条件的{sport}活动",
};

const fr: MessageCatalog = {
  language: "Langue",
  publicNavigation: "Navigation publique",
  appNavigation: "Navigation de l’application",
  mobileNavigation: "Navigation mobile",
  loading: "Chargement des données privées…",
  retry: "Recharger",
  activities: "Activités",
  distance: "Distance",
  movingTime: "Temps en mouvement",
  needsAttention: "À vérifier",
  notRecorded: "Non enregistré",
  yes: "Oui",
  no: "Non",
  cycling: "Cyclisme",
  running: "Course",
  hiking: "Randonnée",
  recorded: "Enregistré",
  derived: "Calculé",
  corrected: "Corrigé",
  missing: "Manquant",
  unsupported: "Non pris en charge",
  insufficient: "Insuffisant",
  source: "Source",
  calculated: "Calculé",
  correction: "Correction",
  none: "Aucun",
  high: "Élevée",
  medium: "Moyenne",
  low: "Faible",
  unavailable: "Indisponible",
  reportTitle: "Rapport d’activité SportTrack",
  limitationsHeading: "Limites et qualité des données",
  noLimitations: "Aucune limite supplémentaire détectée.",
  fixedShareTitle: "Partage fixe SportTrack",
  fixedShareDescription:
    "Cette page affiche uniquement le contenu anonymisé vérifié et figé par l’athlète. Le lien peut être transféré, expirer ou être révoqué.",
  maplessShare: "Partage sans carte",
  maplessDescription: "Cet instantané fixe ne contient aucune géométrie de parcours.",
  routeOverview: "Aperçu du parcours anonymisé",
  includedMetrics: "Mesures incluses",
  recipientBoundary: "Cette page ne peut pas accéder à l’activité ni au fichier source d’origine.",
  eligibleActivity: "{count} activité de {sport} admissible",
  eligibleActivities: "{count} activités de {sport} admissibles",
};

const de: MessageCatalog = {
  language: "Sprache",
  publicNavigation: "Öffentliche Navigation",
  appNavigation: "App-Navigation",
  mobileNavigation: "Mobile App-Navigation",
  loading: "Private Daten werden geladen…",
  retry: "Neu laden",
  activities: "Aktivitäten",
  distance: "Distanz",
  movingTime: "Bewegungszeit",
  needsAttention: "Zu prüfen",
  notRecorded: "Nicht aufgezeichnet",
  yes: "Ja",
  no: "Nein",
  cycling: "Radfahren",
  running: "Laufen",
  hiking: "Wandern",
  recorded: "Aufgezeichnet",
  derived: "Berechnet",
  corrected: "Korrigiert",
  missing: "Fehlt",
  unsupported: "Nicht unterstützt",
  insufficient: "Unzureichend",
  source: "Quelle",
  calculated: "Berechnet",
  correction: "Korrektur",
  none: "Keine",
  high: "Hoch",
  medium: "Mittel",
  low: "Niedrig",
  unavailable: "Nicht verfügbar",
  reportTitle: "SportTrack-Aktivitätsbericht",
  limitationsHeading: "Einschränkungen und Datenqualität",
  noLimitations: "Keine zusätzlichen Einschränkungen erkannt.",
  fixedShareTitle: "Fixierte SportTrack-Freigabe",
  fixedShareDescription:
    "Diese Seite zeigt nur die vom Sportler geprüften und fixierten geschützten Inhalte. Der Link kann weitergegeben werden, ablaufen oder widerrufen werden.",
  maplessShare: "Freigabe ohne Karte",
  maplessDescription: "Dieser fixierte Schnappschuss enthält keine Routengeometrie.",
  routeOverview: "Übersicht der geschützten Route",
  includedMetrics: "Enthaltene Messwerte",
  recipientBoundary:
    "Diese Empfängerseite kann nicht auf die ursprüngliche Aktivität oder Quelldatei zugreifen.",
  eligibleActivity: "{count} geeignete {sport}-Aktivität",
  eligibleActivities: "{count} geeignete {sport}-Aktivitäten",
};

const it: MessageCatalog = {
  language: "Lingua",
  publicNavigation: "Navigazione pubblica",
  appNavigation: "Navigazione dell’app",
  mobileNavigation: "Navigazione mobile",
  loading: "Caricamento delle prove private…",
  retry: "Ricarica",
  activities: "Attività",
  distance: "Distanza",
  movingTime: "Tempo in movimento",
  needsAttention: "Da controllare",
  notRecorded: "Non registrato",
  yes: "Sì",
  no: "No",
  cycling: "Ciclismo",
  running: "Corsa",
  hiking: "Escursionismo",
  recorded: "Registrato",
  derived: "Calcolato",
  corrected: "Corretto",
  missing: "Mancante",
  unsupported: "Non supportato",
  insufficient: "Insufficiente",
  source: "Fonte",
  calculated: "Calcolato",
  correction: "Correzione",
  none: "Nessuno",
  high: "Alta",
  medium: "Media",
  low: "Bassa",
  unavailable: "Non disponibile",
  reportTitle: "Rapporto attività SportTrack",
  limitationsHeading: "Limiti e qualità dei dati",
  noLimitations: "Nessun limite aggiuntivo rilevato.",
  fixedShareTitle: "Condivisione fissa SportTrack",
  fixedShareDescription:
    "Questa pagina mostra solo il contenuto protetto verificato e fissato dall’atleta. Il link può essere inoltrato, scadere o essere revocato.",
  maplessShare: "Condivisione senza mappa",
  maplessDescription: "Questa istantanea fissa non include la geometria del percorso.",
  routeOverview: "Panoramica del percorso protetto",
  includedMetrics: "Metriche incluse",
  recipientBoundary: "Questa pagina non può accedere all’attività o al file sorgente originale.",
  eligibleActivity: "{count} attività di {sport} idonea",
  eligibleActivities: "{count} attività di {sport} idonee",
};

const nl: MessageCatalog = {
  language: "Taal",
  publicNavigation: "Openbare navigatie",
  appNavigation: "App-navigatie",
  mobileNavigation: "Mobiele app-navigatie",
  loading: "Privégegevens laden…",
  retry: "Opnieuw laden",
  activities: "Activiteiten",
  distance: "Afstand",
  movingTime: "Beweegtijd",
  needsAttention: "Te controleren",
  notRecorded: "Niet geregistreerd",
  yes: "Ja",
  no: "Nee",
  cycling: "Fietsen",
  running: "Hardlopen",
  hiking: "Wandelen",
  recorded: "Geregistreerd",
  derived: "Berekend",
  corrected: "Gecorrigeerd",
  missing: "Ontbreekt",
  unsupported: "Niet ondersteund",
  insufficient: "Onvoldoende",
  source: "Bron",
  calculated: "Berekend",
  correction: "Correctie",
  none: "Geen",
  high: "Hoog",
  medium: "Gemiddeld",
  low: "Laag",
  unavailable: "Niet beschikbaar",
  reportTitle: "SportTrack-activiteitenrapport",
  limitationsHeading: "Beperkingen en gegevenskwaliteit",
  noLimitations: "Geen aanvullende beperkingen gevonden.",
  fixedShareTitle: "Vaste SportTrack-deling",
  fixedShareDescription:
    "Deze pagina toont alleen de afgeschermde inhoud die de sporter heeft gecontroleerd en vastgezet. De link kan worden doorgestuurd, verlopen of ingetrokken.",
  maplessShare: "Delen zonder kaart",
  maplessDescription: "Deze vaste momentopname bevat geen routegeometrie.",
  routeOverview: "Overzicht van afgeschermde route",
  includedMetrics: "Opgenomen meetwaarden",
  recipientBoundary:
    "Deze ontvangerspagina heeft geen toegang tot de oorspronkelijke activiteit of het bronbestand.",
  eligibleActivity: "{count} geschikte {sport}-activiteit",
  eligibleActivities: "{count} geschikte {sport}-activiteiten",
};

const es: MessageCatalog = {
  language: "Idioma",
  publicNavigation: "Navegación pública",
  appNavigation: "Navegación de la aplicación",
  mobileNavigation: "Navegación móvil",
  loading: "Cargando datos privados…",
  retry: "Volver a cargar",
  activities: "Actividades",
  distance: "Distancia",
  movingTime: "Tiempo en movimiento",
  needsAttention: "Requiere atención",
  notRecorded: "No registrado",
  yes: "Sí",
  no: "No",
  cycling: "Ciclismo",
  running: "Carrera",
  hiking: "Senderismo",
  recorded: "Registrado",
  derived: "Calculado",
  corrected: "Corregido",
  missing: "Ausente",
  unsupported: "No compatible",
  insufficient: "Insuficiente",
  source: "Fuente",
  calculated: "Calculado",
  correction: "Corrección",
  none: "Ninguno",
  high: "Alta",
  medium: "Media",
  low: "Baja",
  unavailable: "No disponible",
  reportTitle: "Informe de actividad de SportTrack",
  limitationsHeading: "Limitaciones y calidad de los datos",
  noLimitations: "No se detectaron limitaciones adicionales.",
  fixedShareTitle: "Compartición fija de SportTrack",
  fixedShareDescription:
    "Esta página solo muestra el contenido protegido que el deportista revisó y fijó. El enlace puede reenviarse, caducar o revocarse.",
  maplessShare: "Compartir sin mapa",
  maplessDescription: "Esta instantánea fija no incluye la geometría de la ruta.",
  routeOverview: "Vista general de la ruta protegida",
  includedMetrics: "Métricas incluidas",
  recipientBoundary: "Esta página no puede acceder a la actividad ni al archivo fuente originales.",
  eligibleActivity: "{count} actividad de {sport} apta",
  eligibleActivities: "{count} actividades de {sport} aptas",
};

export const messages = { en, "zh-CN": zhCN, fr, de, it, nl, es } as const satisfies Record<
  Locale,
  MessageCatalog
>;

export function message(
  locale: Locale,
  key: MessageKey,
  values?: Readonly<Record<string, string | number>>,
) {
  const template = messages[locale][key];
  return values === undefined ? template : interpolate(template, values);
}
